import { ElementRef, Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';

import { BehaviorSubject, catchError, map, Observable, of, take } from 'rxjs';

import { ConfigService, SentryLogger } from '@fcom/core';
import { FinnairOrder, FinnairPassengerCode } from '@fcom/dapi/api/models';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { getChildAge, getExpediaDateTime, getExpediaHotelLang } from '@fcom/common/utils';
import { ExpediaHotelOrigin } from '@fcom/common/enums';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { generateExpediaPartnerSignature } from '@fcom/dapi/api/order-functions';

import { expediaPartnersConfig, localeToLanguageAndCurrency } from '../../constants';
import { ExpediaConfig } from '../../interfaces';
import { ExpediaHotelsWidgetStatus } from '../../modules/common/enums';

@Injectable({
  providedIn: 'root',
})
export class ExpediaHotelsWidgetService {
  private languageService = inject(LanguageService);
  private sentryLogger = inject(SentryLogger);
  private configService = inject(ConfigService);
  private dapiService = inject(DapiService);

  private readonly iFrameContainerSelector = '#krazyglue-embedded-wrapper';

  private renderer: Renderer2;
  private wrapperObserver?: MutationObserver;
  private DEFAULT_STAY = 3;

  protected readonly WidgetStatus = ExpediaHotelsWidgetStatus;

  private readonly _iframeState$ = new BehaviorSubject<ExpediaHotelsWidgetStatus>(ExpediaHotelsWidgetStatus.INITIAL);
  readonly iframeState$ = this._iframeState$.asObservable();

  public readonly isSupportedLanguage$: Observable<boolean> = this.languageService.locale.pipe(
    map((locale) => locale in localeToLanguageAndCurrency)
  );

  constructor() {
    const rendererFactory = inject(RendererFactory2);

    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public initWidget(order: FinnairOrder, placement: ExpediaHotelOrigin, elementRef: ElementRef): void {
    const { localeValue: locale, langValue: lang } = this.languageService;
    const config = this.getExpediaConfig({
      locale,
      lang,
      placement,
    });

    this._iframeState$.next(ExpediaHotelsWidgetStatus.LOADING);

    const requestUrl = this.buildRequestUrl({
      order,
      config,
    });

    requestUrl.pipe(take(1)).subscribe((url) => {
      if (url) {
        this.createScriptTag(elementRef, url);
      }
    });
  }

  getExpediaConfig({
    locale,
    lang,
    placement,
  }: {
    lang: string;
    locale: string;
    placement: ExpediaHotelOrigin;
  }): ExpediaConfig {
    const { urlPrefix: hotelLanguage } = getExpediaHotelLang(lang);
    const { partnerId, signatureId } = expediaPartnersConfig[placement][hotelLanguage];
    const languageAndCurrency = localeToLanguageAndCurrency[locale] ?? localeToLanguageAndCurrency['en_IE'];

    return { ...languageAndCurrency, partnerId, signatureId };
  }

  private createScriptTag(elementRef: ElementRef, src: string): void {
    // Remove existing script tag if it exists
    const existingScript = elementRef.nativeElement.querySelector('script[src*="xsell-pwa"]');
    if (existingScript) {
      this.renderer.removeChild(elementRef.nativeElement, existingScript);
    }

    // Create new script tag
    const script = this.renderer.createElement('script');
    script.onload = () => this.watchWrapperForIframe(elementRef);
    script.onerror = () => this._iframeState$.next(ExpediaHotelsWidgetStatus.ERROR);

    this.renderer.setAttribute(script, 'src', src);
    this.renderer.setAttribute(script, 'type', 'text/javascript');
    this.renderer.setAttribute(script, 'referrerpolicy', 'origin-when-cross-origin');

    // Append to the component's native element
    this.renderer.appendChild(elementRef.nativeElement, script);
  }

  private watchWrapperForIframe(elementRef: ElementRef): void {
    const wrapper: HTMLElement | null = elementRef.nativeElement.querySelector(this.iFrameContainerSelector);
    if (!wrapper) {
      return;
    }

    this.observeWrapperForIframe(wrapper);
  }

  private observeWrapperForIframe(wrapper: HTMLElement): void {
    this.wrapperObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        const iframe = Array.from(m.addedNodes).find((n) => n instanceof HTMLIFrameElement) as
          | HTMLIFrameElement
          | undefined;

        if (iframe?.src) {
          setTimeout(() => this.wrapperObserver?.disconnect(), 0);
          this.observeIframeInitialResizeReady(iframe).subscribe((isReady) => {
            if (isReady) {
              this._iframeState$.next(ExpediaHotelsWidgetStatus.LOADED);
            } else {
              this._iframeState$.next(ExpediaHotelsWidgetStatus.ERROR);
            }
          });
          break;
        }
      }
    });

    this.wrapperObserver.observe(wrapper, { childList: true, subtree: true });
  }

  /** Build final Expedia URL (signature & all parameters). */
  buildRequestUrl({ order, config }: { order: FinnairOrder; config: ExpediaConfig }): Observable<string> {
    const arrival = order.bounds[0].arrival;
    const arrivalTime = getExpediaDateTime(order.bounds[0].arrival.dateTime);
    let departureTime: string;

    // For one way trips we guess at a 7 day stay at the destination
    if (order.bounds.length === 1) {
      departureTime = getExpediaDateTime(order.bounds[0].arrival.dateTime, this.DEFAULT_STAY * 24);
    } else {
      departureTime = getExpediaDateTime(order.bounds[1].departure.dateTime);
    }

    const bookingStatus = 'confirmed';
    const bookingDateTime = getExpediaDateTime(new Date(order.creationDateTime).toISOString());
    const outboundEndDateTime = arrivalTime;
    const returnStartDateTime = departureTime;
    const destinationTla = arrival.locationCode;

    const childOrInfants = order.passengers.filter(
      (p) => p.passengerTypeCode !== FinnairPassengerCode.ADT && p.passengerTypeCode !== FinnairPassengerCode.YCD
    );

    const numOfAdults = order.passengers.length - childOrInfants.length;
    const numOfChildren = childOrInfants.length;
    const childAges = childOrInfants.map((p) => getChildAge(p.dateOfBirth, arrival.dateTime));

    let queryString =
      `partnerId=${encodeURIComponent(config.partnerId)}` +
      `&outboundEndDateTime=${encodeURIComponent(outboundEndDateTime)}` +
      `&returnStartDateTime=${encodeURIComponent(returnStartDateTime)}` +
      `&destinationTla=${encodeURIComponent(destinationTla)}` +
      `&numOfAdults=${encodeURIComponent(numOfAdults)}` +
      `&locale=${encodeURIComponent(config.languageCode)}` +
      `&currencyCode=${encodeURIComponent(config.currencyCode)}` +
      `&bookingDateTime=${encodeURIComponent(bookingDateTime)}` +
      `&bookingStatus=${encodeURIComponent(bookingStatus)}`;

    if (numOfChildren > 0) {
      queryString += `&numOfChildren=${encodeURIComponent(numOfChildren)}`;
      queryString += `&childAges=${encodeURIComponent(childAges.join(','))}`;
    }

    const signature$ = this.dapiService.invoke$(generateExpediaPartnerSignature, this.configService.cfg.orderUrl, {
      orderId: order.id,
      hash: order.hash,
      body: {
        scriptUrl: `/xsell-pwa.js?${queryString}`,
        placement: config.signatureId,
      },
    });

    return signature$.pipe(
      map(({ signature }) => {
        return `https://xsell.expedia.com/xsell-pwa.js?${queryString}&signature=${signature}`;
      }),
      catchError((err: unknown) => {
        console.error('Error building Expedia URL', err);
        return of('');
      })
    );
  }

  /**
   * Returns an Observable that emits true when the iframe is resized with height and width > 1 because only then it's ready to be shown to the users.
   * If not ready within 5 seconds, it will emit false.
   */
  private observeIframeInitialResizeReady(iframe: HTMLIFrameElement): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width > 1 && entry.contentRect.height > 1) {
            resizeObserver.disconnect();
            observer.next(true);
            observer.complete();
            return;
          }
        }
      });

      resizeObserver.observe(iframe);

      const timeoutFiveSeconds = 5000;
      const timeoutId = setTimeout(() => {
        resizeObserver.disconnect();
        observer.next(false);
        observer.complete();
      }, timeoutFiveSeconds);

      return () => {
        clearTimeout(timeoutId);
        resizeObserver.disconnect();
      };
    }).pipe(
      take(1),
      catchError((error: unknown) => {
        this.sentryLogger.error('Error from Expedia Hotels iframe', error);
        return of(false);
      })
    );
  }
}
